/**
 * watchlist.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable } from "@angular/core";
import { FormControl } from "@angular/forms";
import { BehaviorSubject, Observable } from "rxjs/Rx";

import { AlgorithmService } from "../algorithms/management/algorithm.service";
import { Algorithm } from "../algorithms/structure/algorithm";
import { AlertService } from "../common/alert.service";
import { MessageReceiver } from "../server/message-receiver";
import { MessageTag } from "../server/messages/message-tag";
import { AddReminderRequest, DeleteReminderRequest, RemindersResponse } from "../server/messages/reminders/";
import {
  AcknowledgeWatchlistAlertRequest,
  EditWatchlistItemRequest,
  RefreshWatchlistItemRequest,
  StopWatchingRequest,
  StopWatchingResponse,
  WatchSubjectContextRequest,
  WatchlistItemRequest,
  WatchlistItemUpdatedResponse,
  WatchlistResponse,
} from "../server/messages/watchlists/";
import { MessagingService } from "../server/messaging.service";
import { Subject } from "../subjects/subject";
import { SubjectContextType } from "../subjects/subject-context-type";
import { SubjectReference } from "../subjects/subject-reference";
import { SubjectService } from "../subjects/subject.service";
import { disconnectEmpty, disconnectValue, disconnectWith } from "../support/types/decorators";

import { Group } from "app/auth/identity/group";
import { CreateSharedWorklistRequest } from "app/server/messages/watchlists/create-shared-worklist-request";
import { GroupWorklistsRequest } from "app/server/messages/watchlists/group-worklists-request";
import { GroupWorklistsResponse } from "app/server/messages/watchlists/group-worklists-response";
import { SharedWorklistRequest } from "app/server/messages/watchlists/shared-worklist-request";
import { Displayable } from "app/support/types/aliases";
import { Reminder } from "./reminder";
import { WatchlistEntry, WatchlistTable } from "./watchlist-entry";
import { group } from "console";
import { arrayRemove, getOrInit } from "app/support/utilities";
import { AcknowledgmentResponse, ReplyMessage, ResponseCode } from "app/server/messages/general";
import { EditSharedWorklistRequest } from "app/server/messages/watchlists/edit-shared-worklist-request";
import { Router } from "@angular/router";
import { ReminderLinkedItemRequest } from "app/server/messages/reminders/reminder-linked-item-request";
import { SubjectContextResponse } from "app/server/messages/context-management";

interface WatchedItem
{
  watchId : number;
}

export interface SharedList
{
  title : string;
  description : string;
  worklist? : WatchlistEntry[];
  objectId? : number;

  loading? : boolean;
  loadTime? : number;
}

const WATCH_REFERENCE_TYPE : string = "watch";
const WATCH_REFERENCE_PRIORITY : number = 10;
const REMINDER_REFERENCE_TYPE : string = "reminder";
const REMINDER_REFERENCE_PRIORITY : number = 2;

/**
 * The {@link WatchlistService} holds watchlists -- lists of items the user has
 * elected to subscribe to or that have been assigned to by others. The service
 * also provides means of editing the membership and properties of watchlist
 * items, as well as {@link Reminder}s, which the server manages together.
 */
// tslint:disable-next-line:max-classes-per-file
@Injectable()
export class WatchlistService implements MessageReceiver
{
  /**
   * A map of all watchlist entries by their watch id (which is relative to the
   * specific watchlist in question).
   */
  @disconnectWith("clear")
  public personalEntriesById : Map<number, WatchlistEntry> =
    new Map<number, WatchlistEntry>();
  public activePersonalCount () {
    let count = 0;
    const iter = this.personalEntriesById.values();
    let next : IteratorResult<WatchlistEntry> = iter.next();
    while (!next.done) {
      if (next.value.alert) {
        count++;
      }
      next = iter.next();
    }
    return count;
  }

  /**
   * A map of all shared list entries by an id string generated from both their
   * group id and their list-relative watch id.
   */
  @disconnectWith("clear")
  public sharedEntriesByIds : Map<string, WatchlistEntry> =
    new Map();

  @disconnectWith("clear")
  public hasLoadedSharedLists : Set<number> = new Set();

  /**
   * The raw lists for each shared worklist, by their group ID.
   */
  @disconnectWith("clear")
  public sharedListsByGroup : Map<number, Array<SharedList>> = new Map();
  
  @disconnectWith("clear")
  public objectIdToGroupId : Map<number, number> = new Map();

  /**
   * The entry tables stratify a watchlist's content by subject context type.
   * These tables only exist on the client (to improve UX); the server treats
   * the watchlist as one single collection of potentally-heterogenous types.
   */
  @disconnectEmpty()
  public entryTables : WatchlistTable[] = [];
  public sharedEntryTables : Map<number, WatchlistTable[]> = new Map();

  /**
   * A list of the subject context types seen so far by a read of incoming data.
   * This is used internally to know when to generate a new entry table above.
   * 
   * NOTE: This is used for both personal and shared lists as once a type is 
   * seen, it's adequate to have "stub" tables for those SC types everywhere.
   * In practice, it's not really an issue anyway as the vast majority of users
   * will just have one SC type (healthcare benefits).
   */
  @disconnectEmpty()
  private seenWorklistSCTypes : SubjectContextType[] = [];

  /**
   * The watchlist service is also used to hold {@link Reminder} data received
   * from the server, because it is minimal enough not to merit its own service
   * and is tangentially related.
   */
  @disconnectEmpty()
  public reminders : Reminder[] = [];

  public activeReminderCount () : number {
    return this.reminders.filter(r => r.alert).length;
  }

  /**
   * The server's opaque id for the current user's primary watchlist.
   * NOTE: If/when users have multiple watchlists, this will need renaming or
   * restructuring.
   */
  @disconnectValue(null)
  public personalWorklistObjectId : number;

  @disconnectWith("next", false)
  public hasPersonalList : BehaviorSubject<boolean> = new BehaviorSubject(false);

  /**
   * The server's opaque id for the user's reminder list.
   */
  @disconnectValue(null)
  public reminderListObjectId : number;

  @disconnectEmpty()
  public knownColumns : string[] = [];

  constructor (
    protected messaging : MessagingService,
    protected alerts : AlertService,
    protected algorithms : AlgorithmService,
    protected subjects : SubjectService
  )
  {
    this.messaging.ready.subscribe(
      (on : boolean) => on ? this.connected() : this.disconnected()
    );
  }

  /**
   * Initialization for when the {@link MessagingService} is ready.
   */
  public connected () : void
  {
    this.messaging.receive(MessageTag.RSP_WATCHLIST).subscribe(
      (msg : WatchlistResponse) => {
        if (msg.isShared === true) {
          const groupId = msg.groupId;
          if (!Number.isSafeInteger(groupId)) {
            console.error(`Shared worklist does not have groupId`, msg);
            return;
          }

          // clear the entries for this group
          Array.from(this.sharedEntriesByIds.keys()).forEach((k : string) => {
            if (k.startsWith(`${groupId}~`)) {
              this.sharedEntriesByIds.delete(k);
            }
          });

          this.sharedEntryTables.set(groupId, []);
          msg.worklist.forEach((entry : WatchlistEntry) =>
            this.readSharedWatchlistEntry(entry, groupId));

          // don't generate note controls because we use a popup now
        } else {
          this.personalWorklistObjectId = msg.objectId;
          this.personalEntriesById.clear();
          // this.seenWorklistSCTypes = [];
          this.entryTables = [];

          msg.worklist.forEach((entry : WatchlistEntry) =>
            this.readWatchlistEntry(entry));
  
          this.generateNoteControls();
          this.hasPersonalList.next(true);
        }
      }
    );

    this.messaging.receive(MessageTag.RSP_WATCHLIST_ITEM_UPDATED).subscribe(
      (msg : WatchlistItemUpdatedResponse) => {
        if (msg.objectId !== this.personalWorklistObjectId)
        {
          if (this.objectIdToGroupId.has(msg.objectId)) {
            const groupId = this.objectIdToGroupId.get(msg.objectId); 
            const groupLists = this.sharedListsByGroup.get(msg.groupId) || [];
            const list = groupLists.find(l => l.objectId === msg.objectId);

            const existingEntry : WatchlistEntry = 
              this.sharedEntriesByIds.get(
                this.getMergedId(groupId, msg.watchId));
            if (existingEntry === undefined)
            {
              // use message directly as the entry (it's a valid implementer)
              this.readSharedWatchlistEntry(msg, groupId);
            }
            else
            {
              this.readWatchlistUpdate(existingEntry, msg);
            }
            return;
          }
          throw new Error("Received an update to a non-matching watchlist!");
        } else {
          const existingEntry : WatchlistEntry = 
            this.personalEntriesById.get(msg.watchId);
          if (existingEntry === undefined)
          {
            // use message directly as the entry (it's a valid implementer)
            this.readWatchlistEntry(msg);
          }
          else
          {
            this.readWatchlistUpdate(existingEntry, msg);
          }
          this.generateNoteControls(msg.watchId);
        }
      }
    );

    this.messaging.receive(MessageTag.RSP_STOP_WATCHING).subscribe(
      (msg : StopWatchingResponse) => {
        const isPersonal = msg.objectId === this.personalWorklistObjectId;
        let target : WatchlistEntry;
        let groupId : number;
        if (isPersonal) {
          target = this.personalEntriesById.get(msg.watchId);
          this.personalEntriesById.delete(msg.watchId);
        } else {
          groupId = this.objectIdToGroupId.get(msg.objectId);
          // const groupLists = this.sharedListsByGroup.get(groupId) || [];
          // const list = groupLists.find(l => l.objectId === msg.objectId);
          const pairId = this.getMergedId(groupId, msg.watchId);
          target = this.sharedEntriesByIds.get(pairId);
          this.sharedEntriesByIds.delete(pairId);
        }

        if (target === undefined)
        {
          throw new Error("Cannot remove entry " + msg.watchId + ": not found");
        }
        const tableIndex : number =
          this.seenWorklistSCTypes.indexOf(target.contextType);
        const entryTable = isPersonal ? this.entryTables[tableIndex] :
          this.sharedEntryTables.get(groupId)[tableIndex];
        arrayRemove(entryTable.entries, target);

        const unwatchedSubject : Subject = this.subjects.subjects.find(
          (s : Subject) => s.incomingWatchId === msg.watchId);
        if (unwatchedSubject)
        {
          unwatchedSubject.incomingWatchId = null;
          const i : number = unwatchedSubject.identifiers.findIndex(
            (sr : SubjectReference) => sr.type === "watch");
          if (i > -1)
          {
            unwatchedSubject.identifiers.splice(i, 1);
          }
        }
      }
    );

    this.messaging.receive(MessageTag.RSP_REMINDERS).subscribe(
      (msg : RemindersResponse) => {
        this.reminderListObjectId = msg.objectId;
        msg.notificationList.filter((r : Reminder) => r.alert)
          .forEach((r : Reminder) => {
            const matching : Reminder = this.reminders.find(
              (cmp : Reminder) => cmp.notificationId === r.notificationId);
            if (matching && !matching.alert)
            {
              this.alerts.notify(`Reminder: "${r.summary}"`);
            }
          }
        );
        msg.notificationList.sort(
          (a : Reminder, b : Reminder) =>
            a.triggerTime.valueOf() - b.triggerTime.valueOf()
        );
        this.reminders = msg.notificationList.map((r : Reminder) => {
          r.triggerTime = new Date(r.triggerTime);
          return r;
        });
      }
    );

    this.messaging.receive(MessageTag.RSP_GROUP_WORKLISTS).subscribe(
      (msg : GroupWorklistsResponse) => {
        if (this.sharedListsByGroup.has(msg.groupId)) {
          console.warn("Duplicate GroupWorklistsResponse, overwriting")
          const listsWithEntries = this.sharedListsByGroup.get(msg.groupId)
            .filter((list : SharedList) =>
              Array.isArray(list.worklist) && list.worklist.length > 0);
        } else {
          this.sharedListsByGroup.set(
            msg.groupId,
            msg.sharedWorklists.map(l => ({...l, loading: true})));
          this.hasLoadedSharedLists.add(msg.groupId);
          msg.sharedWorklists.forEach(l =>
            this.objectIdToGroupId.set(l.objectId, msg.groupId));
        }
      }
    );

    // this.messaging.receive(NotificationRemovalMessage).subscribe(
    //   (msg : NotificationRemovalMessage) => {
    //     const target : number = this.notifications.findIndex(
    //       (n : Notification) => n.notificationId === msg.notificationId);
    //     if (target === undefined)
    //     {
    //       throw new Error(
    //         "Cannot remove entry " + msg.notificationId + ": not found");
    //     }
    //     this.notifications.splice(target, 1);
    //   }
    // );
  }

  /**
   * Read and record a {@link WatchlistEntry} coming from a message.
   */
  private readWatchlistEntry (
    entry : WatchlistEntry
  ) : void
  {
    this.personalEntriesById.set(entry.watchId, entry);
    let tableIndex : number =
      this.seenWorklistSCTypes.indexOf(entry.contextType);
    if (tableIndex === -1)
    {
      tableIndex = this.seenWorklistSCTypes.length;
      this.seenWorklistSCTypes.push(entry.contextType);
      entry.summary.forEach(
        (t : Displayable) => this.knownColumns.push(t.displayName));
    }

    if (this.entryTables[tableIndex] 
      && Array.isArray(this.entryTables[tableIndex].entries)) 
    {
      this.entryTables[tableIndex].entries.push(entry);
    } else {
      this.entryTables[tableIndex] = WatchlistEntry.startTable(entry);
    }
  }

  public getMergedId (groupId : number, watchId : number) {
    return `${groupId}~${watchId}`;
  }

  /**
   * Read and record a {@link WatchlistEntry} coming from a message.
   */
  private readSharedWatchlistEntry (
    entry : WatchlistEntry,
    groupId : number
  ) : void
  {
    this.sharedEntriesByIds.set(
      this.getMergedId(groupId, entry.watchId), entry);

    let tableIndex : number =
      this.seenWorklistSCTypes.indexOf(entry.contextType);

    if (tableIndex === -1)
    {
      tableIndex = this.seenWorklistSCTypes.length;
      this.seenWorklistSCTypes.push(entry.contextType);
      entry.summary.forEach(
        (t : Displayable) => this.knownColumns.push(t.displayName));
    }

    const entryTable = getOrInit(this.sharedEntryTables, groupId, []);
    if (entryTable[tableIndex] 
      && Array.isArray(entryTable[tableIndex].entries)) 
    {
      entryTable[tableIndex].entries.push(entry);
    } else {
      entryTable[tableIndex] = WatchlistEntry.startTable(entry);
    }
  }

  /**
   * Update the local data for changes made to a {@link WatchlistEntry}.
   */
  private readWatchlistUpdate (old : WatchlistEntry, updated : WatchlistEntry)
    : void
  {
    if (!old.alert && updated.alert)
    {
      this.alerts.notify(
        `Another user has made changes to one of your subscribed items!
        (The entry will be highlighted in your work list.)`);
    }
    Object.assign(old, updated);
  }

  /**
   * Create form controls for editing the notes of all watchlist entries.
   * TODO: Doing this eagerly is a pretty wasteful; perhaps we should rejigger
   * it to be delayed until a user actually requests an edit...
   */
  private generateNoteControls (targetId? : number) : void
  {
    if (!Number.isInteger(targetId))
    {
      this.personalEntriesById.forEach(
        (entry : WatchlistEntry) => {
          if (!entry.control)
          {
            entry.control = new FormControl(entry.note);
          }
        });
    }
    else
    {
      const entry : WatchlistEntry = this.personalEntriesById.get(targetId);
      if (!entry.control)
      {
        entry.control = new FormControl(entry.note);
      }
    }
  }

  /**
   * Teardown instructions for when the {@link MessagingService} has stopped.
   */
  public disconnected () : void {/* decorator generated */}

  public watchRunningSubject (notify : boolean = true) : Observable<number>
  {
    this.algorithms.assertRunning();
    const subject : Subject = this.algorithms.running.subject;
    return this.watchSubject(subject, notify);
  }

  public watchSubject (subject : Subject, notify : boolean = true)
    : Observable<number>
  {
    if (this.personalWorklistObjectId !== null)
    {
      return this.messaging.resultOf(
        new WatchSubjectContextRequest(this.personalWorklistObjectId, subject.id))
        .flatMap((watched : boolean) => {
          if (watched)
          {
            // TODO: revisit this when cross-subject watchlist edits are a thing
            if (notify)
            {
              this.alerts.success("The subject was added to your personal work list!");
            }
            return this.messaging.nextOfType(
              MessageTag.RSP_WATCHLIST_ITEM_UPDATED
            ).map((msg : WatchlistItemUpdatedResponse) => msg.watchId).do(
              (watchId : number) => {
                subject.addRef(this.createRef(watchId));
                subject.incomingWatchId = watchId;
              }
            );
          }

          if (notify)
          {
            this.alerts.warn(
              "The subject could not be added to your personal work list.");
          }
          return [-1];
        });
    }
    else
    {
      console.error(
        `WatchlistService does not have server ID for watchlist object`,
        this);
      this.alerts.error("Could not add subject; no work list found!");
      return Observable.throw(`WatchlistService does not have server ID for watchlist object`);
    }
  }

  public watchShared (subject : Subject, list : SharedList,  notify : boolean = true)
    : Observable<number>
  {
    if (list !== null && Number.isSafeInteger(list.objectId))
    {
      let request;
      if (Number.isSafeInteger(subject.id)) {
        request = new WatchSubjectContextRequest(list.objectId, subject.id);
      } else {
        const ref = subject.identifiers.find(
          i => i.type === "watch" && i.parentId === list.objectId);
        if (ref) {
          request = new WatchSubjectContextRequest(
            list.objectId, ref.id, ref.parentId);
        } else {
          console.error("Can't find appropiriate referent for ")
        }
      }
      return this.messaging.send(request).flatMap((ar : AcknowledgmentResponse) => {
          if (ar.ok)
          {
            if (notify)
            {
              const groupId = this.objectIdToGroupId.get(list.objectId);
              this.alerts.add({
                message: `The subject was added to list '${list.title}'!`,
                type: "success",
                timeout: "default",
                actionLabel: "View List",
                action: (r : Router) => {
                  r.navigate(["/lists/byGroup", groupId, list.objectId]);
                }
              });
            }
            return this.messaging.nextOfType(
              MessageTag.RSP_WATCHLIST_ITEM_UPDATED
            ).map((msg : WatchlistItemUpdatedResponse) => msg.watchId).do(
              (watchId : number) => {
                // We don't add refs for shared lists at present.
              }
            );
          } else {
            if (notify) {
              if (ar.code === ResponseCode.InvalidRequest) {
                this.alerts.notify(
                  "The subject is already on the selected list.");
              } else {
                this.alerts.warn(
                  "The subject could not be added to the shared list.");
              }
            }
            return [-1];
          }
        });
    }
    else
    {
      console.error(
        `WatchlistService does not have server ID for watchlist object`,
        this);
      this.alerts.error("Could not add subject; no work list found!");
      return Observable.throw(`WatchlistService does not have server ID for watchlist object`);
    }
  }

  /**
   * Build a {@link SubjectReference} for a watchlist entry, from its id alone.
   */
  public createRef (watchId : number, listObjectId?: number) : SubjectReference
  {
    let ref : SubjectReference = {
      id : watchId,
      host : this,
      type : WATCH_REFERENCE_TYPE,
      priority : WATCH_REFERENCE_PRIORITY,
      request : null
    };
    if (listObjectId === undefined) {
      ref.request = new WatchlistItemRequest(
        watchId, this.personalWorklistObjectId);
    } else {
      ref.request = new WatchlistItemRequest(
        watchId, listObjectId);
      ref.parentId = listObjectId;
      ref.summaryURL = `/lists/byGroup/${this.objectIdToGroupId.get(listObjectId)}/${listObjectId}`
    }
    return ref;
  }

  /**
   * Request to stop watching a particular item on the user's watchlist.
   * @param entry either a {@link WatchlistEntry} or a full {@link Subject}
   * @param notify whether to display alerts about the result
   */
  public unwatch (
    entry : WatchlistEntry | Subject,
    notify : boolean = true,
    force : boolean = false,
    objectId : number = this.personalWorklistObjectId
  ) : Observable<boolean>
  {
    const textType = (objectId === this.personalWorklistObjectId ?
              "your personal worklist" : "the shared worklist");
    if (entry instanceof Subject)
    {
      debugger;
      const ref : SubjectReference | null = entry.findRef("watch");
      if (!ref) {
        // TODO: quit, basically
        // return Observable.of([false]);
      }
      return this.messaging.resultOf(
        new StopWatchingRequest(objectId, ref.id, force)
      ).do((unwatched : boolean) => {
        if (unwatched)
        {
          entry.removeRef(ref);
          entry.incomingWatchId = null;
          if (notify)
          {
            this.alerts.notify(
              `The subject has been removed from ${textType}!`);
          }
        }
      });
    }

    return this.messaging.resultOf(
      new StopWatchingRequest(objectId, entry.watchId)
    ).do((unwatched : boolean) => {
      if (unwatched && notify)
      {
        this.alerts.notify(
          `The subject has been removed from ${textType}`);
      }
    });
  }

  /**
   * Dismiss the alert / modified status for a particular watchlist item.
   */
  public clearAlert (
    entry : WatchlistEntry | {watchId : number},
    objectId : number = this.personalWorklistObjectId
  )
    : Observable<boolean>
  {
    return this.messaging.resultOf(
      new AcknowledgeWatchlistAlertRequest(
        objectId, entry.watchId));
  }

  /**
   * Request a watchlist item to regenerate its summary data from the actual
   * subject context data. (NOTE: This method is automatically requested
   * whenever a save operation occurs or an algorithm is launched.)
   */
  public resummarize (
    entry : WatchlistEntry | {watchId : number},
    objectId : number = this.personalWorklistObjectId
  )
    : Observable<boolean>
  {
    return this.messaging.resultOf(
      new RefreshWatchlistItemRequest(objectId, entry.watchId));
  }

  private findSharedEntry (
    entry : {watchId: number},
    objectId : number
  ) : WatchlistEntry | null
  {
    const shared : boolean = objectId !== this.personalWorklistObjectId;
    if (shared) {
      try {
        const groupId : number = this.objectIdToGroupId.get(objectId);
        const lists = this.sharedListsByGroup.get(groupId);
        const list : SharedList = lists.find(l => l.objectId === objectId);
        const foundEntry = list.worklist.find(e => e.watchId === entry.watchId);
        return foundEntry;
      } catch (e) {
        return null;
      }
    } else {
      return this.personalEntriesById.get(entry.watchId);
    }
  }

  /**
   * Request a change to the note field of a watchlist item.
   */
  public editNote (
    entry : WatchlistEntry,
    note : string,
    categories : string[] = [],
    objectId : number = this.personalWorklistObjectId
  ) : Observable<boolean>
  {
    return this.messaging.resultOf(
      new EditWatchlistItemRequest(
        objectId,
        entry.watchId,
        note,
        categories,
        entry.emailNotifyOnUpdate
      )).do((success : boolean) => {
        if (success) {
          entry.note = note;
        }
      });
  }

  public editSharedList (
    objectId : number,
    changes : {
      title? : string;
      description? : string;
    }
  ) : Observable<boolean>
  {
    return this.messaging.resultOf(
      new EditSharedWorklistRequest(
        objectId,
        changes.title,
        changes.description)
    );
  }

  /**
   * Request a change to a watchlist item's notification setting.
   * NOTE: This is the same protocol request as modifying the note (above), but
   * the UI constrains them to being separate atomic operations to simplify the
   * interface.
   */
  public changeNotificationSetting (
    entry : WatchlistEntry,
    notify : boolean,
    objectId : number = this.personalWorklistObjectId
  ) : Observable<boolean>
  {
    return this.messaging.resultOf(
      new EditWatchlistItemRequest(
        objectId,
        entry.watchId,
        entry.note,
        entry.categories,
        notify
      ));
  }

  /**
   * Create a new reminder with the specified summary and trigger time.
   */
  public addReminder (summary : string, triggerTime : Date, linkedId? : number)
    : Observable<boolean>
  {
    if (this.reminderListObjectId !== null)
    {
      return this.messaging.resultOf(
        new AddReminderRequest(
          this.reminderListObjectId,
          summary,
          triggerTime.valueOf(),
          linkedId
        ));
    }
    else
    {
      console.error(
        `WatchlistService does not have server ID for reminder list`,
        this);
      this.alerts.error("Could not add reminder; no reminder list found!");
    }
  }

  /**
   * Request to remove a reminder.
   */
  public removeReminder (id : number) : Observable<boolean>
  {
    return this.messaging.resultOf(
      new DeleteReminderRequest(this.reminderListObjectId, id));
  }

  public openReminderLinkedRecord (reminder : Reminder, target? : Algorithm) : Observable<Subject>
  {
    const subject : Subject = this.subjects.fromReference(
			this.createReminderRef(reminder.notificationId));
    if (!target) {
      target = this.algorithms.defaultForOpen(reminder.linkedType);
      subject.initialAlgorithmTarget = target;
    }
    return this.subjects.open(subject, true);
  }

  /**
   * Build a {@link SubjectReference} for a reminder entry, from its id alone.
   */
  public createReminderRef (reminderId : number) : SubjectReference
  {
    let ref : SubjectReference = {
      id : reminderId,
      host : this,
      type : REMINDER_REFERENCE_TYPE,
      priority : REMINDER_REFERENCE_PRIORITY,
      request : new ReminderLinkedItemRequest(
        this.reminderListObjectId, reminderId)
    };
    return ref;
  }
  

  /**
   * Sugar for determining if there are active alerts, effectively meaning:
   *  - any reminder is past its trigger time, OR
   *  - any watchlist item has unacknowledged modifications
   */
  public get hasAlerts () : boolean
  {
    return (this.reminders.some((r : Reminder) => r.alert)
      || Array.from(this.personalEntriesById.values())
        .some((e : WatchlistEntry) => e.alert));
  }

  /**
   * Determine the number of active alerts (see above). This shortcut is
   * utilized by the templates to generate small 'badges' with notification
   * counts in them.
   */
  public get alertCount () : number
  {
    let count : number = this.reminders.filter(
      (r : Reminder) => r.alert).length;
    this.personalEntriesById.forEach((e : WatchlistEntry) => {
      if (e.alert)
      {
        count++;
      }
    });
    return count;
  }

  /**
   * Load and (optionally) navigate to the subject that a watchlist entry
   * refers to.
   */
  public open (
	  entry : WatchlistEntry,
	  navigate : boolean = true,
	  target? : Algorithm
	) : Observable<Subject>
	{
		const subject : Subject = this.subjects.fromReference(
			this.createRef(entry.watchId));
		// subject.summary = entry.summary;
		if (target)
		{
			subject.initialAlgorithmTarget = target;
		}
		return this.subjects.open(subject, navigate);
	}
  
  /**
   * Load and (optionally) navigate to the subject that a watchlist entry
   * refers to.
   */
  public openShared (
	  entry : WatchlistEntry,
    groupId: number,
	  navigate : boolean = true,
	  target? : Algorithm
	) : Observable<Subject>
	{
		const subject : Subject = this.subjects.fromReference(
			this.createRef(entry.watchId, groupId));
		// subject.summary = entry.summary;
		if (target)
		{
			subject.initialAlgorithmTarget = target;
		}
		return this.subjects.open(subject, navigate);
	}

  /* ===== SHARED LISTS ===== */
  public requestListsForGroup (
    group : Group | number
  ) : Observable<SharedList[]>
  {
    const id = (typeof group === 'object') ? group.id : group;
    return this.messaging.converse(new GroupWorklistsRequest(id), error => {
      console.error(`Error retrieving worklist for group ${id}\n`, error);
    }).map((rsp : GroupWorklistsResponse) => {
      this.sharedListsByGroup.set(rsp.groupId, rsp.sharedWorklists);
      this.hasLoadedSharedLists.add(rsp.groupId);
      rsp.sharedWorklists.forEach(l =>
        this.objectIdToGroupId.set(l.objectId, rsp.groupId));
      return rsp.sharedWorklists;
    });
  }

  public requestListsForAll (groups : Group[]) {
    return Observable.zip(...groups.map(g => this.requestListsForGroup(g.id)));
  }

  private addListToGroup (
    gid: number,
    list: SharedList,
    annotateAsLoaded : boolean = false
  ) : SharedList 
  {
    if (annotateAsLoaded) {
      list.loading = false;
      list.loadTime = Date.now();
    }
    const existingForGroup = this.sharedListsByGroup.get(gid) || [];
    const i = existingForGroup.findIndex(l => l.objectId === list.objectId);
    existingForGroup[i > -1 ? i : existingForGroup.length] = list;
    return list;
  }

  public loadSharedList (gid: number, opaqueId: number) : Observable<SharedList>
  {
    return this.messaging.converse(
      new SharedWorklistRequest(gid, opaqueId)).map(
        (reply : WatchlistResponse & SharedList) =>
          this.addListToGroup(gid, reply, true));
  }

  public createSharedList (config : {
    groupId : number,
    title : string,
    description : string,
    stableUrl? : string
  }) : Observable<SharedList> {
    return this.messaging.converse(new CreateSharedWorklistRequest(
      config.groupId,
      config.title,
      config.description,
      config.stableUrl
    )).map((rsp : WatchlistResponse & SharedList) => 
      this.addListToGroup(config.groupId, rsp, true));
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/watchlist/watchlist.service.ts